export type DevelopmentRegion = 'shared-dev';
export type StagingRegion = 'staging6' | 'staging8';

export const STAGING_REGIONS: readonly StagingRegion[] = ['staging6', 'staging8'] as const;
export type ProductionRegion = 'us' | 'eu' | 'ca' | 'au';

export type Region = DevelopmentRegion | StagingRegion | ProductionRegion;

export const REGIONS: Region[] = ['us', 'eu', 'ca', 'au', 'shared-dev', 'staging6', 'staging8'];

/**
 * Single source of truth for "is this an unvalidated value a known Region?" —
 * shared by the router's search validation and the region-selection fallback
 * logic so an arbitrary/malformed `datacenter` value can never be treated as
 * a real region just because it happened to be a non-empty string.
 */
export function isRegion(value: unknown): value is Region {
  return typeof value === 'string' && REGIONS.includes(value as Region);
}

type EnvironmentRegionMap = {
  local: readonly DevelopmentRegion[];
  development: readonly DevelopmentRegion[];
  staging6: readonly StagingRegion[];
  production: readonly ProductionRegion[];
};

const ENVIRONMENT_REGIONS: EnvironmentRegionMap = {
  local: ['shared-dev'],
  development: ['shared-dev'],
  staging6: ['staging6', 'staging8'],
  production: ['us', 'eu', 'ca', 'au'],
} as const;

export type EnvironmentKey = keyof EnvironmentRegionMap;

export function getAvailableRegions(environment?: string): readonly Region[] {
  const env = (environment || import.meta.env.VITE_SENTRY_ENV || 'local') as EnvironmentKey;

  return ENVIRONMENT_REGIONS[env] || ENVIRONMENT_REGIONS.local;
}

export type RegionsForEnvironment<T extends EnvironmentKey> = EnvironmentRegionMap[T][number];

export interface RegionMetadata {
  code: Region;
  label: string;
  flag: string;
}

export const REGION_METADATA: Record<Region, RegionMetadata> = {
  us: {
    code: 'us',
    label: 'United States',
    flag: '🇺🇸',
  },
  eu: {
    code: 'eu',
    label: 'European Union',
    flag: '🇪🇺',
  },
  ca: {
    code: 'ca',
    label: 'Canada',
    flag: '🇨🇦',
  },
  au: {
    code: 'au',
    label: 'Australia',
    flag: '🇦🇺',
  },
  'shared-dev': {
    code: 'shared-dev',
    label: 'Shared Dev',
    flag: '🔧',
  },
  staging6: {
    code: 'staging6',
    label: 'Staging 6',
    flag: '🧪',
  },
  staging8: {
    code: 'staging8',
    label: 'Staging 8',
    flag: '🧪',
  },
};
